import { memo, useEffect, useMemo, useRef } from 'react'
import ReactECharts from 'echarts-for-react'
import type { EChartsOption } from 'echarts'
import type { Time } from 'lightweight-charts'
import type { ScreenerCandle } from '@/types'

interface PriceChartProps {
  candles: ScreenerCandle[]
  symbol: string
  exchange?: string
  onCrosshairMove?: (time: Time | null, price: number | null) => void
  syncedTime?: Time | null
  height?: number
}

interface PriceDataPoint {
  tsMs: number
  open: number
  high: number
  low: number
  close: number
}

function PriceChartComponent({
  candles,
  symbol: _symbol,
  exchange,
  onCrosshairMove,
  syncedTime,
  height = 300,
}: PriceChartProps) {
  const chartRef = useRef<ReactECharts>(null)

  const derived = useMemo(() => {
    const sorted = [...candles].sort((a, b) => a.timestamp - b.timestamp)
    const priceSeries: PriceDataPoint[] = sorted.map(c => ({
      tsMs: c.timestamp * 1000,
      open: c.open,
      high: c.high,
      low: c.low,
      close: c.close,
    }))
    const tsMs = priceSeries.map(item => item.tsMs)
    return { priceSeries, tsMs }
  }, [candles])

  const nearestIndex = useMemo(() => {
    const values = derived.tsMs
    return (target: number): number => {
      if (values.length === 0) return -1
      let left = 0
      let right = values.length - 1
      while (left <= right) {
        const mid = (left + right) >> 1
        const value = values[mid]
        if (value === target) return mid
        if (value < target) left = mid + 1
        else right = mid - 1
      }
      if (left >= values.length) return values.length - 1
      if (left <= 0) return 0
      const prev = values[left - 1]
      const next = values[left]
      return Math.abs(prev - target) <= Math.abs(next - target) ? left - 1 : left
    }
  }, [derived.tsMs])

  const option = useMemo<EChartsOption>(() => {
    const seriesData = derived.priceSeries.map(c => [c.tsMs, c.open, c.close, c.low, c.high])

    return {
      animation: false,
      backgroundColor: 'transparent',
      textStyle: { color: '#d1d4dc' },
      grid: {
        left: 8,
        right: 8,
        top: 30,
        bottom: 26,
        containLabel: true,
      },
      axisPointer: {
        link: [{ xAxisIndex: [0] }],
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'cross' },
        backgroundColor: 'rgba(17, 24, 39, 0.95)',
        borderColor: '#374151',
        textStyle: { color: '#e5e7eb', fontSize: 11 },
        formatter: (params: any) => {
          const list = Array.isArray(params) ? params : [params]
          const candleEntry = list.find((p: any) => p.seriesName === 'Price')
          const rawData = candleEntry?.data as number[] | undefined
          const axisMs = Number(candleEntry?.axisValue ?? 0)

          if (!rawData || rawData.length < 5) {
            return `<div style="color:#9ca3af;">${new Date(axisMs).toLocaleString()}</div>`
          }

          const open = Number(rawData[1])
          const close = Number(rawData[2])
          const low = Number(rawData[3])
          const high = Number(rawData[4])
          const change = open !== 0 ? ((close - open) / open) * 100 : 0
          const changeColor = change >= 0 ? '#26a69a' : '#ef5350'

          const formatPrice = (value: number) =>
            value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 6 })

          return `
            <div style="color:#9ca3af;margin-bottom:6px;">${new Date(axisMs).toLocaleString()}</div>
            <div>Open: <span style="color:#f3f4f6;font-weight:600;">$${formatPrice(open)}</span></div>
            <div>High: <span style="color:#26a69a;font-weight:600;">$${formatPrice(high)}</span></div>
            <div>Low: <span style="color:#ef5350;font-weight:600;">$${formatPrice(low)}</span></div>
            <div>Close: <span style="color:#f3f4f6;font-weight:600;">$${formatPrice(close)}</span></div>
            <div>Change: <span style="color:${changeColor};font-weight:600;">${change >= 0 ? '+' : ''}${change.toFixed(2)}%</span></div>
          `
        },
      },
      xAxis: {
        type: 'time',
        axisLine: { lineStyle: { color: '#2B2B43' } },
        axisLabel: {
          color: '#9ca3af',
          hideOverlap: true,
        },
        splitLine: { lineStyle: { color: '#2B2B43' } },
      },
      yAxis: {
        type: 'value',
        position: 'right',
        axisLine: { lineStyle: { color: '#2B2B43' } },
        splitLine: { lineStyle: { color: '#2B2B43' } },
        axisLabel: {
          color: '#9ca3af',
          formatter: (value: number) => value.toLocaleString(undefined, { maximumFractionDigits: 6 }),
        },
      },
      dataZoom: [
        {
          type: 'inside',
          xAxisIndex: [0],
          zoomOnMouseWheel: true,
          moveOnMouseWheel: true,
          moveOnMouseMove: true,
          preventDefaultMouseMove: false,
          filterMode: 'none',
        },
        {
          type: 'inside',
          yAxisIndex: [0],
          zoomOnMouseWheel: 'shift',
          moveOnMouseMove: 'shift',
          moveOnMouseWheel: false,
          preventDefaultMouseMove: false,
          filterMode: 'none',
        },
        {
          type: 'slider',
          xAxisIndex: [0],
          show: false,
          start: 0,
          end: 100,
          filterMode: 'none',
        },
        {
          type: 'slider',
          yAxisIndex: [0],
          show: false,
          start: 0,
          end: 100,
          filterMode: 'none',
        },
      ],
      series: [
        {
          name: 'Price',
          type: 'candlestick',
          itemStyle: {
            color: '#26a69a',
            color0: '#ef5350',
            borderColor: '#26a69a',
            borderColor0: '#ef5350',
          },
          data: seriesData,
        },
      ],
    }
  }, [derived.priceSeries])

  useEffect(() => {
    if (!onCrosshairMove) return
    const instance = chartRef.current?.getEchartsInstance()
    if (!instance) return

    const handler = (event: any) => {
      const axisInfo = event?.axesInfo?.[0]
      if (!axisInfo) return
      const axisMs = Number(axisInfo.value)
      if (!Number.isFinite(axisMs)) return

      const idx = nearestIndex(axisMs)
      const point = idx >= 0 ? derived.priceSeries[idx] : null
      onCrosshairMove(Math.floor(axisMs / 1000) as Time, point?.close ?? null)
    }
    const outHandler = () => onCrosshairMove(null, null)

    instance.on('updateAxisPointer', handler)
    instance.on('mouseout', outHandler)
    return () => {
      instance.off('updateAxisPointer', handler)
      instance.off('mouseout', outHandler)
    }
  }, [onCrosshairMove, derived.priceSeries, nearestIndex])

  useEffect(() => {
    const instance = chartRef.current?.getEchartsInstance()
    if (!instance) return
    if (syncedTime === undefined) return

    if (syncedTime === null) {
      instance.dispatchAction({ type: 'hideTip' })
      return
    }

    const targetMs = Number(syncedTime) * 1000
    if (!Number.isFinite(targetMs) || derived.tsMs.length === 0) return

    const idx = nearestIndex(targetMs)
    if (idx >= 0) {
      instance.dispatchAction({ type: 'showTip', seriesIndex: 0, dataIndex: idx })
    }
  }, [syncedTime, derived.tsMs, nearestIndex])

  // Get current price for display
  const currentPrice = candles.length > 0 ? candles[candles.length - 1].close : null
  const prevPrice = candles.length > 1 ? candles[candles.length - 2].close : null
  const priceChange = currentPrice && prevPrice ? ((currentPrice - prevPrice) / prevPrice) * 100 : 0
  const priceColor = priceChange >= 0 ? '#26a69a' : '#ef5350'

  return (
    <div className="relative">
      <div className="absolute top-1.5 left-1.5 z-10 flex items-center gap-2">
        <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wide">
          Price {exchange && <span className="text-xs text-gray-500">({exchange})</span>}
        </span>
        {currentPrice !== null && (
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-white">
              ${currentPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 6 })}
            </span>
            <span className="text-xs font-medium" style={{ color: priceColor }}>
              {priceChange >= 0 ? '+' : ''}{priceChange.toFixed(2)}%
            </span>
          </div>
        )}
      </div>

      <ReactECharts
        ref={chartRef}
        option={option}
        style={{ height, width: '100%' }}
        notMerge
        lazyUpdate
      />
    </div>
  )
}

export const PriceChart = memo(PriceChartComponent)
